import React, { useEffect, useMemo, useState } from "react";
import { useGlobalContext } from "../context/AuthContext";
import { FaRegSave } from "react-icons/fa";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_VERSION,
  CREATE_EDIT,
  DELETE_VERSION,
  GET_ALL_GISTS,
  GET_COMMENT,
  GET_COMMENTS_FLAT,
  CREATE_COMMENT,
  SOFT_REMOVE_COMMENT,
  DELETE_COMMENT_THREAD,
  GET_COMMENT_ANCHORS,
  CREATE_ANCHORED_COMMENT,
} from "../../services/graphql/queriesMutations";

import { CommentAnchorType, EditType, VersionType } from "../../services/types";
import { CgCloseR } from "react-icons/cg";
import RichEditor from "../dashboard/RichEditor";
import ReplyModal from "../others/ReplyModal";
import CountComponent from "../others/CountComponent";
import FlagComponent from "../others/FlagComponent";
import FavComponent from "../others/FavComponent";
import SendComment from "../comments/SendComment";
import { CommentExplorer, CommentType } from "../../features/comments";
import { AnnotatedBody } from "../../features/anchored-comments";
import type { AnchoredSelectionDraft } from "../../features/anchored-comments";
import { Modal } from "antd";
import { useSwipeable } from "react-swipeable";
import Navigation from "../others/Navigation";

type EditProps = {
  edits: EditType[];
  versionData: VersionType;
  newVersionData: string;
  createVersion: boolean;
  setCreateVersion: (value: boolean) => void;
  versionCurrentIndex: number;
  setVersionCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  editCurrentIndex: number;
  setEditCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  gistCurrentIndex: number;
  versionsLength: number;
  gistLength: number;
  isFocusMode: boolean;
  onEnterFocusMode: () => void;
  onExitFocusMode: () => void;
  showComments?: boolean;
};

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});


function mergeCommentAnchorList(
  anchors: CommentAnchorType[],
  nextAnchor: CommentAnchorType
): CommentAnchorType[] {
  const byId = new Map<number, CommentAnchorType>();
  anchors.forEach((anchor) => byId.set(anchor.id, anchor));
  byId.set(nextAnchor.id, nextAnchor);
  return Array.from(byId.values()).sort((a, b) => {
    const byCreatedAt = Date.parse(a.createdAt || "") - Date.parse(b.createdAt || "");
    if (Number.isFinite(byCreatedAt) && byCreatedAt !== 0) return byCreatedAt;
    return a.id - b.id;
  });
}

function getGeneralCommentList(
  comments: CommentType[],
  anchors: CommentAnchorType[]
): CommentType[] {
  const childrenByParentId = new Map<number, CommentType[]>();
  const anchoredRootIds = new Set<number>();

  anchors.forEach((anchor) => {
    if (typeof anchor.rootCommentId === "number") {
      anchoredRootIds.add(anchor.rootCommentId);
    }
  });

  comments.forEach((comment) => {
    if (typeof comment.parentId === "number") {
      const siblings = childrenByParentId.get(comment.parentId) || [];
      siblings.push(comment);
      childrenByParentId.set(comment.parentId, siblings);
    }
  });

  const hiddenIds = new Set<number>();
  const visit = (commentId: number) => {
    if (hiddenIds.has(commentId)) return;
    hiddenIds.add(commentId);
    (childrenByParentId.get(commentId) || []).forEach((child) => visit(child.id));
  };

  anchoredRootIds.forEach((rootId) => visit(rootId));
  return comments.filter((comment) => !hiddenIds.has(comment.id));
}

const Edit: React.FC<EditProps> = ({
  edits,
  versionData,
  newVersionData,
  versionCurrentIndex,
  setVersionCurrentIndex,
  editCurrentIndex,
  setEditCurrentIndex,
  gistCurrentIndex,
  gistLength,
  isFocusMode,
  onEnterFocusMode,
  onExitFocusMode,
  showComments = true,
}) => {
  const [createNewVersion] = useMutation(CREATE_VERSION, {
    refetchQueries: [{ query: GET_ALL_GISTS }],
  });

  const [deleteNewVersion] = useMutation(DELETE_VERSION, {
    refetchQueries: [{ query: GET_ALL_GISTS }],
  });

  const [createEdit] = useMutation(CREATE_EDIT, {
    refetchQueries: [{ query: GET_ALL_GISTS }],
  });

  const { textareaEdit, setTextareaEdit, user } = useGlobalContext();

  const [commentsArray, setCommentsArray] = useState<CommentType[]>([]);
  const [content, setContent] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [richtextEdit, setRichtextEdit] = useState<boolean>(false);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const activeEdit = edits?.[editCurrentIndex] || edits?.[0];
  const activeEditId = activeEdit?.id;
  const activeGistId = versionData.gistId;

  const {
    data: flatCommentsData,
    loading: flatCommentsLoading,
    error: flatCommentsError,
    refetch: refetchFlatComments,
  } = useQuery(GET_COMMENTS_FLAT, {
    variables: {
      gistId: activeGistId ?? 0,
    },
    skip: !activeGistId || !showComments,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  // Safety fallback: some local/dev sessions may still be running an older backend
  // process that does not know commentsFlat yet. Keep the legacy nested query alive
  // so the new Comment Explorer can still render instead of showing an error.
  const {
    data: legacyCommentsData,
    loading: legacyCommentsLoading,
    error: legacyCommentsError,
    refetch: refetchLegacyComments,
  } = useQuery(GET_COMMENT, {
    variables: {
      gistId: activeGistId ?? 0,
      editId: activeEditId ?? null,
    },
    skip: !activeGistId || !showComments,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: commentAnchorsData,
    loading: commentAnchorsLoading,
    error: commentAnchorsError,
    refetch: refetchCommentAnchors,
  } = useQuery(GET_COMMENT_ANCHORS, {
    variables: {
      gistId: activeGistId ?? 0,
      editId: activeEditId ?? null,
      status: "ACTIVE",
    },
    skip: !activeGistId || !activeEditId || !showComments || richtextEdit,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const remoteCommentAnchors = useMemo<CommentAnchorType[]>(
    () => commentAnchorsData?.commentAnchors ?? [],
    [commentAnchorsData]
  );
  const [localCommentAnchors, setLocalCommentAnchors] = useState<CommentAnchorType[]>([]);

  const commentAnchors: CommentAnchorType[] = useMemo(() => {
    const byId = new Map<number, CommentAnchorType>();
    remoteCommentAnchors.forEach((anchor) => byId.set(anchor.id, anchor));
    localCommentAnchors.forEach((anchor) => byId.set(anchor.id, anchor));
    return Array.from(byId.values()).sort((a, b) => {
      const byCreatedAt = Date.parse(a.createdAt || "") - Date.parse(b.createdAt || "");
      if (Number.isFinite(byCreatedAt) && byCreatedAt !== 0) return byCreatedAt;
      return a.id - b.id;
    });
  }, [remoteCommentAnchors, localCommentAnchors]);

  const generalCommentsArray = useMemo(
    () => getGeneralCommentList(commentsArray, commentAnchors),
    [commentsArray, commentAnchors]
  );

  const commentsLoading = flatCommentsLoading || legacyCommentsLoading;
  const commentsError = flatCommentsError && legacyCommentsError ? flatCommentsError : undefined;

  const refetchComments = async (explicitGistId?: number) => {
    if (!showComments) {
      setCommentsArray([]);
      return;
    }

    const gistIdToLoad = explicitGistId ?? activeGistId;

    if (!gistIdToLoad) {
      console.log("⚠️ refetchComments: No valid gist ID, clearing comments");
      setCommentsArray([]);
      return;
    }

    console.log("🔄 Refetching comments for gistId:", gistIdToLoad, "current editId:", activeEditId);

    try {
      const response = await refetchFlatComments({
        gistId: gistIdToLoad,
      });
      const nextComments = response.data?.commentsFlat;

      if (Array.isArray(nextComments)) {
        console.log("✅ Flat comments refetched:", nextComments.length);
        setCommentsArray(nextComments);
        return;
      }
    } catch (error) {
      console.warn("⚠️ Flat comments refetch failed; falling back to legacy comments query:", error);
    }

    try {
      const response = await refetchLegacyComments({
        gistId: gistIdToLoad,
        editId: activeEditId ?? null,
      });
      const nextComments = response.data?.comments || [];
      console.log("✅ Legacy comments refetched:", nextComments.length);
      setCommentsArray(nextComments);
    } catch (error) {
      console.error("❌ Failed to refetch comments:", error);
    }
  };

  const [createComment] = useMutation(CREATE_COMMENT);
  const [createAnchoredComment] = useMutation(CREATE_ANCHORED_COMMENT);

  const [softRemoveComment] = useMutation(SOFT_REMOVE_COMMENT);
  const [deleteCommentThread] = useMutation(DELETE_COMMENT_THREAD);

  const isCommentModerator = user?.userType === "admin" || user?.userType === "moderator";

  const handleSoftRemoveComment = async (commentId: number) => {
    if (!user) {
      alert("Please login to remove comments.");
      return;
    }

    try {
      await softRemoveComment({
        variables: { id: commentId },
      });
      await refetchComments();
    } catch (error: any) {
      console.error("❌ Failed to soft-remove comment:", error);
      alert(error?.message || "Failed to remove comment. Please try again.");
    }
  };

  const handleHardDeleteCommentThread = async (commentId: number) => {
    if (!isCommentModerator) {
      alert("Only moderators and administrators can permanently delete comment threads.");
      return;
    }

    try {
      const result = await deleteCommentThread({
        variables: { id: commentId },
      });

      const message = result?.data?.deleteCommentThread?.message;
      if (message) console.log(`✅ ${message}`);

      await refetchComments();
    } catch (error: any) {
      console.error("❌ Failed to hard-delete comment thread:", error);
      alert(error?.message || "Failed to delete comment thread. Please try again.");
    }
  };

  const handleReply = async (parentId: number, content: string) => {
    if (!content.trim()) return;

    if (!user) {
      alert("Please login to reply to comments");
      return;
    }

    console.log(
      "💬 Creating reply with parentId:",
      parentId,
      "editId:",
      activeEditId
    );

    try {
      const result = await createComment({
        variables: {
          newComment: {
            comment: content,
            parentId: parentId,
            gistId: activeGistId,
            editId: activeEditId,
          },
        },
      });
      console.log("✅ Reply created:", result);
      await refetchComments();
    } catch (error: any) {
      console.error("❌ Failed to create reply:", error);
      alert(error?.message || "Failed to create reply. Please try again.");
    }
  };

  const handleCreateAnchoredComment = async (draft: AnchoredSelectionDraft, comment: string) => {
    if (!comment.trim()) return;

    if (!user) {
      alert("Please login to create anchored comments.");
      return;
    }

    if (!activeGistId || !activeEditId) {
      throw new Error("A Gist and Edit are required to create an anchored comment.");
    }

    try {
      const result = await createAnchoredComment({
        variables: {
          input: {
            gistId: activeGistId,
            editId: activeEditId,
            comment,
            target: "BODY",
            selectedText: draft.selectedText,
            selectedHash: draft.selectedHash,
            startOffset: draft.startOffset,
            endOffset: draft.endOffset,
            blockIndex: draft.blockIndex,
            prefixText: draft.prefixText,
            suffixText: draft.suffixText,
            contextText: draft.contextText,
          },
        },
      });

      const createdAnchor = result.data?.createAnchoredComment as CommentAnchorType | undefined;
      if (createdAnchor) {
        setLocalCommentAnchors((previousAnchors) => mergeCommentAnchorList(previousAnchors, createdAnchor));
      }

      await Promise.all([
        refetchComments(),
        refetchCommentAnchors({
          gistId: activeGistId,
          editId: activeEditId,
          status: "ACTIVE",
        }),
      ]);

      return createdAnchor;
    } catch (error: any) {
      console.error("❌ Failed to create anchored comment:", error);
      throw new Error(error?.message || "Failed to create anchored comment. Please try again.");
    }
  };

  const stringSizeInBytes = (str: string) => {
    const encoder = new TextEncoder();
    return encoder.encode(str).length;
  };

  const handleNext = () => {
    if (!edits?.length) return;
    setEditCurrentIndex((prevIndex) => (prevIndex + 1) % edits.length);
  };

  const handlePrev = () => {
    setEditCurrentIndex((prevIndex) =>
      prevIndex === 0 ? edits.length - 1 : prevIndex - 1
    );
  };

  const handleIndexChange = (newIndex: number) => {
    setEditCurrentIndex(newIndex);
  };

  const handleCreateVersion = async () => {
    const contentSize = parseFloat(
      (stringSizeInBytes(content) / (1024 * 1024)).toFixed(2)
    );
    if (contentSize > 5) {
      Modal.error({
        title: "Data too large",
        content: `Your content is ${contentSize} MBs, which must be less then 5 MBs`,
      });
      return;
    }

    if (!newVersionData) {
      Modal.error({
        title: "Validation Error",
        content: "Version cannot be empty",
      });
      return;
    }

    if (
      newVersionData === versionData.point &&
      content === edits[editCurrentIndex]?.body
    ) {
      setRichtextEdit(false);
      setTextareaEdit(false);
      return;
    }

    if (
      newVersionData !== versionData.point &&
      content === edits[editCurrentIndex]?.body
    ) {
      let new_version_id: number | undefined;
      try {
        const versionResponse = await createNewVersion({
          variables: {
            version: {
              gistId: versionData.gistId,
              point: newVersionData,
            },
          },
        });

        new_version_id = versionResponse.data.addVersion.id;

        if (new_version_id) {
          await createEdit({
            variables: {
              edit: {
                versionId: new_version_id,
                body: content,
              },
            },
          });
        }
      } catch (e) {
        try {
          if (new_version_id) {
            await deleteNewVersion({
              variables: {
                id: new_version_id,
              },
            });
          }
        } catch (innerError) {
          console.log("version not created successfully", innerError);
        }
      }
      setTextareaEdit(false);
      setEditCurrentIndex(0);
      setVersionCurrentIndex(0);
      refetchComments();
      return;
    }

    if (
      newVersionData !== versionData.point &&
      content !== edits[editCurrentIndex]?.body
    ) {
      let new_version_id: number | undefined;
      try {
        const versionResponse = await createNewVersion({
          variables: {
            version: {
              gistId: versionData.gistId,
              point: newVersionData,
            },
          },
        });

        new_version_id = versionResponse.data.addVersion.id;

        if (new_version_id) {
          await createEdit({
            variables: {
              edit: {
                versionId: new_version_id,
                body: content,
              },
            },
          });
        }
      } catch (e) {
        try {
          if (new_version_id) {
            await deleteNewVersion({
              variables: {
                id: new_version_id,
              },
            });
          }
        } catch (innerError) {
          console.log("version not created successfully", innerError);
        }
      }

      setRichtextEdit(false);
      setTextareaEdit(false);
      setEditCurrentIndex(0);
      setVersionCurrentIndex(0);
      refetchComments();
      return;
    }

    if (
      newVersionData === versionData.point &&
      content !== edits[editCurrentIndex]?.body
    ) {
      if (richtextEdit) {
        await createEdit({
          variables: {
            edit: {
              versionId: versionData.id,
              body: content,
            },
          },
        });
      }

      setRichtextEdit(false);
      setEditCurrentIndex(0);
      refetchComments();
      return;
    }
  };

  const handleCancelCreateVersion = () => {
    newVersionData = versionData.point;
    setContent(activeEdit?.body || "");
    setRichtextEdit(false);
    setTextareaEdit(false);
  };

  const handleSwipe = useSwipeable({
    onSwiping: (event) => {
      setIsDragging(true);
      setDragOffset(event.deltaX);
    },
    onSwiped: (event) => {
      setIsDragging(false);
      if (event.deltaX > 100) {
        handlePrev();
      } else if (event.deltaX < -100) {
        handleNext();
      }
      setDragOffset(0);
    },
    onTap: () => {
      setIsDragging(false);
      setDragOffset(0);
    },
    // Mouse drag is disabled so users can select/copy text without moving the carousel.
    // Touch swipes still work on mobile; desktop navigation remains available through controls.
    trackMouse: false,
  });

  useEffect(() => {
    if (!edits?.length) {
      return;
    }

    const currentEdit = edits[editCurrentIndex] || edits[0];
    if (!richtextEdit && currentEdit) {
      setContent(currentEdit.body);
    }
  }, [edits, editCurrentIndex, richtextEdit]);

  useEffect(() => {
    if (!activeGistId || !showComments) {
      setCommentsArray([]);
      return;
    }

    refetchComments(activeGistId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGistId, showComments]);

  useEffect(() => {
    setLocalCommentAnchors([]);
  }, [activeGistId, activeEditId]);

  useEffect(() => {
    setEditCurrentIndex(0);
  }, [versionCurrentIndex, gistCurrentIndex, gistLength, setEditCurrentIndex]);

  useEffect(() => {
    if (flatCommentsData?.commentsFlat && !flatCommentsError) {
      console.log(
        "🔄 Auto-syncing flat comments from query:",
        flatCommentsData.commentsFlat.length,
        "for gistId:",
        activeGistId,
        "current editId:",
        activeEditId
      );
      setCommentsArray(flatCommentsData.commentsFlat || []);
      return;
    }

    if (legacyCommentsData?.comments) {
      console.log(
        "🔄 Auto-syncing legacy nested comments fallback:",
        legacyCommentsData.comments.length,
        "for gistId:",
        activeGistId,
        "current editId:",
        activeEditId
      );
      setCommentsArray(legacyCommentsData.comments || []);
    }
  }, [flatCommentsData, flatCommentsError, legacyCommentsData, activeGistId, activeEditId]);

  if (!edits || edits.length === 0) {
    return (
      <div className="py-6 text-center" style={{ color: "var(--color-gray-warm)" }}>
        No edits available.
      </div>
    );
  }

  const editCarouselStyle: React.CSSProperties = {
    transform: `translateX(${dragOffset}px)`,
    transition: isDragging ? "none" : "transform 0.3s ease-in-out",
    opacity: isDragging ? 0.96 : 1,
  };

  const commentsNavigatorBlock = (
    <>
      <div className={`gist-comments-wrap ${isFocusMode ? "is-focus-mode" : ""}`}>
        <SendComment
          setShowModal={setShowModal}
          editId={activeEdit?.id}
          gistId={activeGistId}
          userId={activeEdit?.user?.id}
          handleRefetchComments={refetchComments}
        />
      </div>

      <div className={`gist-comment-deck-wrap ${isFocusMode ? "is-focus-mode" : ""}`}>
        <CommentExplorer
          key={`comments-gist-${activeGistId}-${generalCommentsArray.length}`}
          gistId={activeGistId}
          comments={generalCommentsArray}
          editId={activeEdit?.id}
          loading={commentsLoading}
          error={commentsError}
          currentUserId={user?.id}
          currentUserRole={user?.userType}
          onReply={user ? handleReply : undefined}
          onSoftRemove={user ? handleSoftRemoveComment : undefined}
          onHardDeleteThread={isCommentModerator ? handleHardDeleteCommentThread : undefined}
        />
      </div>
    </>
  );

  return (
    <div style={{ fontFamily: "var(--font-primary)" }}>
      <div style={editCarouselStyle} {...handleSwipe}>
        <div className="gist-body-divider" />

        <div className="gist-edit-header">
          <div className="gist-person">
            <img
              src={activeEdit?.user?.image || "/profile.png"}
              className="gist-avatar"
              style={{ border: "2px solid var(--color-border)" }}
              alt="User avatar"
            />
            <div className="gist-person-copy">
              <div className="gist-person-name">{activeEdit?.user?.name}</div>
              <div className="gist-person-date">
                {activeEdit?.createdAt &&
                  dateFormatter.format(Date.parse(activeEdit.createdAt))}
              </div>
            </div>
          </div>

          <div className="gist-edit-actions">
            <div className="gist-action-icons">
              <FlagComponent flag={activeEdit?.flag} editId={activeEdit?.id} />
              <FavComponent editId={activeEdit?.id} />
            </div>

            {(richtextEdit || textareaEdit) && user && (
              <div className="gist-save-actions">
                <button
                  disabled={!user}
                  onClick={() => handleCancelCreateVersion()}
                  className="gist-icon-button gist-icon-button-cancel"
                  type="button"
                >
                  <CgCloseR />
                </button>
                <button
                  disabled={!user}
                  onClick={() => handleCreateVersion()}
                  className="gist-icon-button gist-icon-button-save"
                  type="button"
                >
                  <FaRegSave />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="gist-body-toolbar">
          <div className="gist-votes-row">
            <CountComponent
              label="N"
              count={activeEdit?.newnessCount}
              editId={activeEdit?.id}
            />
            <CountComponent
              label="I"
              count={activeEdit?.importantCount}
              editId={activeEdit?.id}
            />
            <CountComponent
              label="Q"
              count={activeEdit?.qualityCount}
              editId={activeEdit?.id}
            />
          </div>

          <div className="gist-level-controls">
            <div className="gist-level-chip">Edit</div>
            <Navigation
              currentIndex={editCurrentIndex}
              totalItems={edits.length}
              onChangeIndex={handleIndexChange}
              handlePrev={handlePrev}
              handleNext={handleNext}
            />
          </div>
        </div>

        {edits?.length > 0 && (
          <div
            className={`gist-body-content ${richtextEdit ? "is-editing" : isFocusMode ? "is-focus" : "is-preview"}`}
            onDoubleClick={() => user && setRichtextEdit(true)}
          >
            {richtextEdit ? (
              <RichEditor
                editable={richtextEdit}
                content={content}
                setContent={setContent}
              />
            ) : (
              <AnnotatedBody
                html={content}
                anchors={commentAnchors}
                loading={commentAnchorsLoading}
                error={commentAnchorsError}
                commentsVisible={showComments}
                gistId={activeGistId}
                editId={activeEdit?.id}
                comments={commentsArray}
                commentsLoading={commentsLoading}
                commentsError={commentsError}
                currentUserId={user?.id}
                currentUserRole={user?.userType}
                canCreateAnchoredComment={Boolean(user && activeGistId && activeEditId && showComments)}
                onCreateAnchoredComment={user ? handleCreateAnchoredComment : undefined}
                onReply={user ? handleReply : undefined}
                onSoftRemove={user ? handleSoftRemoveComment : undefined}
                onHardDeleteThread={isCommentModerator ? handleHardDeleteCommentThread : undefined}
              />
            )}

            {!richtextEdit && (
              <>
                {!isFocusMode && (
                  <span className="gist-body-actions-inline">
                    <span className="gist-read-more-ellipsis"> …</span>
                    <button
                      className="gist-read-more-inline"
                      type="button"
                      onClick={onEnterFocusMode}
                    >
                      Read more →
                    </button>
                  </span>
                )}

                {isFocusMode && (
                  <div className="gist-body-actions">
                    <button
                      className="gist-read-more is-secondary"
                      type="button"
                      onClick={onExitFocusMode}
                    >
                      Back to browsing
                    </button>
                  </div>
                )}
              </>
            )}
            
            {isFocusMode && showComments && (
              <section
                id={`gist-comments-for-gist-${activeGistId}`}
                data-current-edit-id={activeEdit?.id}
                className="gist-comments-section is-focus-mode"
              >
                {commentsNavigatorBlock}
              </section>
            )}
          </div>
        )}
      </div>

      {!isFocusMode && showComments && (
        <section
          id={`gist-comments-for-gist-${activeGistId}`}
          data-current-edit-id={activeEdit?.id}
          className="gist-comments-section is-browsing-mode"
        >
          {commentsNavigatorBlock}
        </section>
      )}

      {showModal && (
        <ReplyModal gist_id={versionData.gistId} setShowModal={setShowModal} />
      )}
    </div>
  );
};

export default Edit;
